import * as XLSX from 'xlsx'

export interface ExcelQuestionRow {
  Pertanyaan: string
  'Jawaban A': string
  'Jawaban B': string
  'Jawaban C': string
  'Jawaban D': string
  'Jawaban E': string
  'Normal Lab': string
  Pembahasan: string
}

export interface ParsedQuestion {
  question: string
  discussion: string
  normal_lab: string
  image: string,
  answers: Array<{
    choice: string
    is_true: boolean
  }>
}

export const parseExcelFile = async (file: File): Promise<ParsedQuestion[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]

        // Convert to JSON with header row
        const jsonData: ExcelQuestionRow[] = XLSX.utils
          .sheet_to_json(worksheet, {
            header: 1,
            defval: '',
          })
          .slice(1) as any // Skip header row

        // Parse the data
        const parsedQuestions = parseQuestionsFromExcel(jsonData)
        resolve(parsedQuestions)
      } catch (error) {
        reject(
          new Error('Failed to parse Excel file: ' + (error as Error).message),
        )
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsArrayBuffer(file)
  })
}

const parseQuestionsFromExcel = (data: any[]): ParsedQuestion[] => {
  const questions: ParsedQuestion[] = []

  data.forEach((row, index) => {
    // Skip empty rows
    if (!row[0] && !row[1] && !row[2] && !row[3] && !row[4] && !row[5]) {
      return
    }

    try {
      const question = row[0]?.toString().trim() || ''
      const answerA = row[1]?.toString().trim() || ''
      const answerB = row[2]?.toString().trim() || ''
      const answerC = row[3]?.toString().trim() || ''
      const answerD = row[4]?.toString().trim() || ''
      const answerE = row[5]?.toString().trim() || ''
      const normalLab = row[6]?.toString().trim() || ''
      const discussion = row[7]?.toString().trim() || ''

      if (!question) {
        throw new Error(`Row ${index + 2}: Question is required`)
      }

      // Parse answers and determine which one is correct
      const answers: Array<{ choice: string; is_true: boolean }> = []

      const answerOptions = [
        { text: answerA, label: 'A' },
        { text: answerB, label: 'B' },
        { text: answerC, label: 'C' },
        { text: answerD, label: 'D' },
        { text: answerE, label: 'E' },
      ]

      let hasCorrectAnswer = false

      answerOptions.forEach(({ text, label }) => {
        if (text) {
          // Check if this answer is marked as correct with [x] or [X]
          const isCorrect = /^\[x\]/i.test(text)
          const cleanText = text.replace(/^\[x\]\s*/i, '').trim()

          if (!cleanText) {
            throw new Error(
              `Row ${
                index + 2
              }: Answer ${label} cannot be empty after removing [x] marker`,
            )
          }

          answers.push({
            choice: cleanText,
            is_true: isCorrect,
          })

          if (isCorrect) {
            hasCorrectAnswer = true
          }
        }
      })

      if (answers.length < 2) {
        throw new Error(`Row ${index + 2}: At least 2 answers are required`)
      }

      if (!hasCorrectAnswer) {
        throw new Error(
          `Row ${
            index + 2
          }: At least one answer must be marked as correct with [x]`,
        )
      }

      questions.push({
        question,
        discussion,
        image: null,
        normal_lab: normalLab,
        answers,
      })
    } catch (error) {
      throw new Error(`Row ${index + 2}: ${(error as Error).message}`)
    }
  })

  if (questions.length === 0) {
    throw new Error('No valid questions found in the Excel file')
  }

  return questions
}

export const validateExcelFile = (file: File): string | null => {
  // Check file type
  const allowedTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ]

  if (!allowedTypes.includes(file.type)) {
    return 'Please select a valid Excel file (.xlsx or .xls)'
  }

  // Check file size (max 5MB)
  const maxSize = 5 * 1024 * 1024
  if (file.size > maxSize) {
    return 'File size must be less than 5MB'
  }

  return null
}

export const generateTemplateData = (): ExcelQuestionRow[] => {
  return [
    {
      Pertanyaan: 'Apa ibu kota Indonesia?',
      'Jawaban A': '[x] Jakarta',
      'Jawaban B': 'Surabaya',
      'Jawaban C': 'Bandung',
      'Jawaban D': 'Medan',
      'Jawaban E': 'Yogyakarta',
      'Normal Lab': '100-200 mg/dL',
      Pembahasan: 'Jakarta adalah ibu kota Indonesia sejak kemerdekaan.',
    },
    {
      Pertanyaan: 'Siapa presiden pertama Indonesia?',
      'Jawaban A': 'Suharto',
      'Jawaban B': '[x] Soekarno',
      'Jawaban C': 'BJ Habibie',
      'Jawaban D': 'Megawati',
      'Jawaban E': 'SBY',
      'Normal Lab': '',
      Pembahasan: 'Soekarno adalah presiden pertama Republik Indonesia.',
    },
  ]
}

export const downloadTemplate = () => {
  const templateData = generateTemplateData()

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.json_to_sheet(templateData)

  // Set column widths
  const colWidths = [
    { wch: 50 }, // Pertanyaan
    { wch: 20 }, // Jawaban A
    { wch: 20 }, // Jawaban B
    { wch: 20 }, // Jawaban C
    { wch: 20 }, // Jawaban D
    { wch: 20 }, // Jawaban E
    { wch: 20 }, // Normal Lab
    { wch: 50 }, // Pembahasan
  ]
  ws['!cols'] = colWidths

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Questions')

  // Download file
  XLSX.writeFile(wb, 'template-questions.xlsx')
}
